import React, { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { fetchIncidentAnalysisData } from '../../../../../../../utils/api/reportAPI';
import CurrentUserReportTable from './Table/CurrentUserReport';
import APIErrorComponent from '../../../../../../components/_shared/Error/APIErrorComponent';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import axios from 'axios';
import { useSelector } from 'react-redux';

const CurrentUserReport = () => {
  const location = useLocation();
  const queryClient = useQueryClient()
  const { token } = useSelector((state) => state?.auth);
  const [sort, setSort] = useState(null)
  const [tableOptions, setTableOptions] = useState({
    rowPerPages: 10,
    activePage: 0,
  });

  const handleRowsPerPage = (e) => {
    setTableOptions((prev) => ({
      ...prev,
      rowPerPages: e.target.value,
      activePage: 0,
    }));
  };

  const handlePageClick = (event) => {
    setTableOptions((prev) => ({ ...prev, activePage: event.selected }));
  };

  const getUserCurrentReports = useCallback(async () => {
    let apiQueryParams = {
      per_page: tableOptions.rowPerPages,
      page: tableOptions.activePage + 1,
      code: location.state?.code
    };
    if (sort && sort.columnName && sort.order) {
      apiQueryParams = {
          ...apiQueryParams,
          order: sort.order,
          sort: sort.columnName,
      }
  }

    return fetchIncidentAnalysisData({...apiQueryParams,export:'N'});
  }, [tableOptions.rowPerPages, tableOptions.activePage, location.state?.code,sort]);

  const {
    isLoading,
    isFetching,
    data,
    isError,
    error,
  } = useQuery(
    ['get-user-report', tableOptions, location.state?.code, sort],
    getUserCurrentReports,
    {
      retry: (failureCount, error) => {
        const status = error?.response?.status;
        return failureCount < 1 && status >= 400 && status < 600;
      },
      retryDelay: 1000, 
    }
  );
  

  const pageCount = Math.ceil(data?.total / tableOptions.rowPerPages);
  const tableColumn = ['Full Name', 'Company', 'Email', 'Phone', 'Congregation', 'Device Count'];

 const downloadPDF = async () => {
  try {
    const payload = {
      code: location.state?.code,
      export: 'N',
      pagination: 'false', 
    };

    const response = await axios.post(
      `${process.env.REACT_APP_API_BACKEND_SERVICE_URL}/reports/report_data`,
      payload,
      {
        headers: {
          'Content-type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
      }
    );

    const fullData = response?.data?.data || [];

    if (!Array.isArray(fullData) || fullData.length === 0) {
      return; // no toast, simply exit
    }

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    });

    doc.setFontSize(18);
    doc.text('Current User Report', 14, 22);

    doc.setFontSize(12);
    doc.text(
      `Congregation: ${fullData[0]?.user_congregation || 'N/A'}`,
      14,
      30
    );

    const tableColumn = [
      'Full Name',
      'Company',
      'Email',
      'Phone',
      'Congregation',
      'Device Count',
    ];

    const tableRows = fullData.map((item) => [
      item?.name_tx || '',
      item?.company_tx || '',
      item?.email_tx || '',
      item?.phone_tx || '',
      item?.user_congregation || '',
      item?.device_count || 0,
    ]);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 40,
      theme: 'striped',
      headStyles: {
        fillColor: [0, 81, 242],
        textColor: [255, 255, 255],
        fontSize: 12,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 10,
        cellPadding: 5,
      },
      alternateRowStyles: {
        fillColor: [240, 240, 240],
      },
      styles: {
        lineColor: [0, 0, 0],
        lineWidth: 0.5,
        halign: 'center',
      },
      columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 40 },
        2: { cellWidth: 60 },
        3: { cellWidth: 40 },
        4: { cellWidth: 60 },
        5: { cellWidth: 20 },
      },
    });

    doc.save('current-user-report.pdf');

  } catch (error) {
    console.error('PDF export error:', error);
  }
};


  const handleColumnSort = async (column, sortDirection) => {
    setSort({
        columnName: column.sortField,
        order: sortDirection,
    })
    // No need to manually invalidate - React Query will handle the sort dependency change
}

const downloadExcelFile = (data, fileName = 'current-user-report') => {
  const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${fileName}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const downloadExcel = async () => {
  try {
    const payload = {
      code: location.state?.code,
      export: 'Y',          
      pagination: 'false',  
    };

    const response = await axios.post(
      `${process.env.REACT_APP_API_BACKEND_SERVICE_URL}/reports/report_data`,
      payload,
      {
        headers: {
          'Content-type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
      }
    );

    if (!response?.data) return;

    downloadExcelFile(response.data, 'current-user-report');

  } catch (error) {
    console.error('Excel export error:', error);
  }
};

  return (
    <>
      {isError ? (
        <APIErrorComponent error={error} />
      ) : (
        <>
          <CurrentUserReportTable
            isLoading={isLoading || isFetching}
            data={data?.data}
            tableOptions={tableOptions}
            totaldata={data?.total}
            handleColumnSort={handleColumnSort}
            pageCount={pageCount}
            handlePageClick={handlePageClick}
            handleRowsPerPage={handleRowsPerPage}
            downloadPDF={downloadPDF}
            downloadExcel={downloadExcel}
            reportTableData={data?.data} 
            reportData={{ code: location.state?.code }} 
            reportCode={location.state?.code}          
            generatePDFFunction={downloadPDF}        
          />
        </>
      )}
    </>
  );
};

export default CurrentUserReport;
